---
title: "Candidate search in Chosen"
description: "Search candidates already in Chosen with one query bar and three modes — AI deep search (the default), smart, and boolean. When to reach for each one."
category: "search"
keywords:
  - "candidate search"
  - "ATS search"
  - "AI candidate search"
  - "boolean candidate search"
  - "natural language candidate search"
schema: "HowTo"
nav:
  section: "guides"
  order: 20
---

Type a sentence. Search figures out what you meant. Usually.

Chosen has one query bar with three modes. **AI deep search** is the default: a slower, multi-stage pipeline that reads resumes and ranks candidates by fit. **Smart search** is instant and reads plain English. **Boolean search** is for when you want exact control over `AND`, `OR`, and `NOT`.

The bar has a toggle — a lightning bolt for AI search, a magnifying glass for standard (smart or boolean). Whichever you used last is where the bar opens next time, for the client app and the recruiter portal separately.

All three run against candidates already in your system — not the open web. To go find new people, that's [sourcing](/guides/sourcing).

## In this section

<Steps>
  <li>**[Smart, boolean, and deep search](/guides/search/smart-vs-deep)** — the three modes and which one to reach for. Start here.</li>
  <li>**[Boolean search syntax](/guides/search/boolean-syntax)** — operators, quoted phrases, and copy-pasteable examples.</li>
  <li>**[How a deep search run works](/guides/search/deep-search-runs)** — what the AI pipeline does, why results stream in, and the 150-candidate cap.</li>
  <li>**[Saved searches](/guides/search/saved-searches)** — re-run a query, rename it, delete it.</li>
  <li>**[Why a search returns no results](/guides/search/why-no-results)** — the usual causes, and the fix for each.</li>
</Steps>

<Callout type="tip" title="The first query is rarely the best query">
Run one. Look at the top ten results. Refine the wording. Search responds to how you describe the role, so a second pass is normal — not a sign you did it wrong.
</Callout>
